import { numberToMonth as formatNumberToMonth } from '@shared/utils';
import { isCalendarDayInstant } from '@shared/utils/core/date';
import dayjs from 'dayjs';
import { DateTime } from 'luxon';

export function isoToDayJs(isoDate: string | null | undefined): dayjs.Dayjs | null {
    return isoDate ? dayjs(isoDate) : null;
}

export function dateToDayJs(date: Date | null | undefined): dayjs.Dayjs | null {
    return date ? dayjs(date) : null;
}

/**
 * Reads a date-only value (a calendar day stored at UTC noon, as `DatePicker`/`RangePicker` write
 * with `dateOnly`) back as local midnight of that day. Read as an instant it would fall on the
 * next day east of UTC+12. Any other instant, including one recorded before a field stored
 * calendar days, is returned as the local time it names.
 */
export function calendarDayToDayJs(date: Date | null | undefined): dayjs.Dayjs | null {
    if (!date) return null;
    if (!isCalendarDayInstant(date)) return dayjs(date);
    return dayjs(new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

export { isCalendarDayInstant };

export function numberToMonth(month: number, monthFormat: any = 'MMMM'): string {
    return formatNumberToMonth(month, monthFormat);
}

/**
 * Returns the start of the following LOCAL day for use as an exclusive upper
 * bound. The input must carry the selected calendar day in the viewer's local
 * timezone, as produced by a `RangePicker` without `dateOnly`. `dateOnly` values
 * use UTC noon and must not be passed here.
 *
 * Not interchangeable with UTC day-boundary helpers: adding one calendar day in
 * the local timezone also preserves the intended day across DST transitions.
 */
export function startOfNextLocalDay(date: Date): Date {
    return DateTime.fromJSDate(date).startOf('day').plus({ days: 1 }).toJSDate();
}

/**
 * Midnight on the Sunday that starts the week containing `date`. The task calendar's month grid and
 * week view share this Sunday-first week; Luxon's own `startOf('week')` is the ISO Monday.
 */
export function startOfCalendarWeek(date: DateTime): DateTime {
    return date.startOf('day').minus({ days: date.weekday % 7 });
}
